import { ArrowLeftOutlined, EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Button, Card, Col, Collapse, Image, List, Modal, Row, Space, Tooltip, Typography } from 'antd';

import { DeploymentRequestInfoPosted } from 'components/Create/common/DeploymentRequestInfoPosted';
import { Loader } from 'components/common/Loader';
import WimsicalError from 'components/common/WimsicalError/WimsicalError';
import { formatErrorMessage } from 'helpers/formatErrorMessage';
import Cookies from 'js-cookie';
import { QueryErrorModel } from 'models/ErrorModel';
import { SparingRequest } from 'models/SparingRequests';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSparingOrdersQuery, useGetSparingRequestQuery } from 'redux/services/twittlebugs/sparingRequestsApi';
import { resetAttachments } from 'redux/slices/attachmentsSlice';
import { setFullOutageAddress, setFullShippingAddress } from 'redux/slices/formSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { colors, toRgba } from 'styles/colors';
import { CollapseRowHeader } from './components/CollapseRowHeader';
import { DefectiveReturnsInformationCard } from './components/DefectiveReturnsInformationCard';
import { PartsInformationCard } from './components/PartsInformationCard';
import { ResendNotificationsButton } from './components/ResendNotificationsButton';
import { TrackingInformationCard } from './components/StatusInformationCard';
import { UpdateSparingOrderModal } from './components/UpdateSparingOrderModal';
import { UpdateTrackingInfoModal } from './components/UpdateTrackingInfoModal';
const { Title } = Typography;

export const PostedPage = (): JSX.Element => {
  const cookie = Cookies.get('isLocalTimeZone');
  const parsedCookie = cookie ? JSON.parse(cookie) : false;
  const { id } = useParams();
  const { data, isLoading, isError, error } = useGetSparingRequestQuery(id ?? skipToken);
  const dispatch = useAppDispatch();
  const [isLocalTimeZone, setIsLocalTimeZone] = useState(parsedCookie);

  const { user } = useAppSelector((state) => state.app);

  const isMdsiEmployee = user && user['https://acuity.mdsiinc.com/user/user_metadata'].is_mdsi_employee;

  const { data: sparingOrders, isLoading: isOrdersLoading } = useGetSparingOrdersQuery(id as string, { skip: !id || !data || isLoading });

  const [isOpen, setIsOpen] = useState('');
  const [clickedIdx, setClickedId] = useState<number | undefined>(undefined);
  const nav = useNavigate();

  useEffect(() => {
    return () => {
      dispatch(setFullOutageAddress(null));
      dispatch(setFullShippingAddress(null));
      dispatch(resetAttachments());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeZoneToggle = (): void => {
    if (isLocalTimeZone) {
      Cookies.set('isLocalTimeZone', 'false', { expires: 365 });
      setIsLocalTimeZone(false);
    } else {
      Cookies.set('isLocalTimeZone', 'true', { expires: 365 });
      setIsLocalTimeZone(true);
    }
  };

  if (isLoading || isOrdersLoading) return <Loader loadingMessage="Loading sparing request..." />;

  if (isError) {
    const err = error as QueryErrorModel;

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Row justify={'space-between'} style={{ width: '100%' }}>
          <Col>
            <Typography.Title level={4}>Open Sparing Request Information</Typography.Title>
          </Col>
          <Col>
            <Space>
              <Button onClick={(): void => nav('/search')} icon={<ArrowLeftOutlined />}>
                Back to Search
              </Button>
            </Space>
          </Col>
        </Row>
        <WimsicalError title={formatErrorMessage(typeof err.status === 'number' ? err.status : 400)} subTitle={err?.data?.errorMessage ? err.data.errorMessage : ''} statusCode={err.status} />
      </Space>
    );
  }

  return (
    <Row style={{ width: '100%' }}>
      <Row justify={'space-between'} style={{ width: '100%' }}>
        <Col>
          <Typography.Title level={4}>Open Sparing Request Information</Typography.Title>
        </Col>
        <Col>
          <Space>
            <Button onClick={(): void => nav('/search')} icon={<ArrowLeftOutlined />}>
              Back to Search
            </Button>
            {!!data?.attachments.length && (
              <Button
                onClick={(): void => {
                  Modal.info({
                    icon: undefined,
                    width: 800,
                    content: (
                      <Card
                        headStyle={{ backgroundColor: toRgba(colors.romanSilver, 0.4) }}
                        bodyStyle={{ minHeight: 259 }}
                        title={
                          <Title level={4} style={{ fontWeight: 'normal' }}>
                            File Attachments
                          </Title>
                        }>
                        <Row style={{ marginTop: 5 }}>
                          <Col span={24} style={{ minHeight: '100%' }}>
                            {data?.attachments?.map((attachment) => (
                              <Card bodyStyle={{ padding: 8 }} key={attachment.name} style={{ marginBottom: 5 }}>
                                <Row align="middle" justify="space-between">
                                  <Col>
                                    <Image
                                      fallback={
                                        'data:image/svg+xml;base64,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'
                                      }
                                      width={48}
                                      height={48}
                                      src={(attachment as unknown as { url: string }).url}
                                    />
                                    <Typography.Link href={(attachment as unknown as { url: string }).url} style={{ marginLeft: 15 }}>
                                      {attachment.name}
                                    </Typography.Link>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                          </Col>
                        </Row>
                      </Card>
                    )
                  });
                }}>
                Attachments
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Row justify={'space-between'} style={{ width: '100%' }}>
        <Col>
          <DeploymentRequestInfoPosted values={data as SparingRequest} />
        </Col>
      </Row>
      <Col span={24}>
        <Card
          headStyle={{ backgroundColor: toRgba(colors.illuminatingEmerald, 0.3) }}
          title={
            <Row justify="space-between" align={'middle'}>
              <Col>
                <Title level={4} style={{ fontWeight: 'normal', margin: 0, padding: 0 }}>
                  Order Information
                </Title>
              </Col>
            </Row>
          }>
          <Row gutter={[10, 10]}>
            {sparingOrders?.data.map((order, idx) => {
              return (
                <Col key={order.id} span={24}>
                  <Collapse className="narrow-collapse">
                    <Collapse.Panel
                      header={<CollapseRowHeader sparingOrder={order} />}
                      extra={
                        <Space>
                          <Button
                            icon={
                              <Tooltip title="Update Order Information">
                                <EditOutlined />
                              </Tooltip>
                            }
                            onClick={(e): void => {
                              e.stopPropagation();
                              setClickedId(idx);
                              setIsOpen('headerInfo');
                            }}
                          />
                          <ResendNotificationsButton order={order} />
                          {isMdsiEmployee && (
                            <Button
                              icon={
                                <Tooltip title="Status Info">
                                  <HistoryOutlined />
                                </Tooltip>
                              }
                              onClick={(e): void => {
                                e.stopPropagation();
                                Modal.info({
                                  title: 'Status Information:',
                                  content: (
                                    <List
                                      className="hide-scrollbar"
                                      style={{ height: 750, overflowY: 'auto' }}
                                      locale={{ emptyText: 'No Updates Yet' }}
                                      dataSource={order?.auditHistory}
                                      renderItem={(item): JSX.Element => <List.Item>{item}</List.Item>}
                                    />
                                  ),
                                  width: 750,
                                  okText: 'Close',
                                  closable: true
                                });
                              }}
                            />
                          )}
                        </Space>
                      }
                      key={1}>
                      <Row gutter={[16, 0]}>
                        <Col xs={24} xl={12}>
                          <PartsInformationCard lineItems={order.lineItems} />
                        </Col>
                        <Col xs={24} xl={12}>
                          <TrackingInformationCard
                            extra={
                              <Tooltip title="Update Tracking Info">
                                <Button
                                  onClick={(e): void => {
                                    e.stopPropagation();
                                    setClickedId(idx);
                                    setIsOpen('trackingInfo');
                                  }}
                                  icon={<EditOutlined />}
                                />
                              </Tooltip>
                            }
                            trackingInfo={order.trackingInformation}
                          />
                        </Col>
                      </Row>
                    </Collapse.Panel>
                  </Collapse>
                </Col>
              );
            })}
          </Row>
        </Card>
      </Col>
      {data?.defectiveReturns && data?.sparingRequestType?.isActivateDefectiveReturns ? (
        <Col span={24}>
          <DefectiveReturnsInformationCard defectiveReturns={data?.defectiveReturns} parentData={data} sparingOrders={sparingOrders?.data} />
        </Col>
      ) : null}
      <UpdateSparingOrderModal
        isOpen={isOpen === 'headerInfo'}
        setIsOpen={setIsOpen}
        sparingOrder={sparingOrders ? sparingOrders.data[clickedIdx ?? 0] : undefined}
        afterClose={(): void => setClickedId(undefined)}
      />
      <UpdateTrackingInfoModal
        isOpen={isOpen === 'trackingInfo'}
        setIsOpen={setIsOpen}
        sparingOrder={sparingOrders ? sparingOrders.data[clickedIdx ?? 0] : undefined}
        afterClose={(): void => setClickedId(undefined)}
      />
    </Row>
  );
};
